/**
 * webcam-recorder/history.js — persistent recordings library.
 *
 * Pattern lifted from interview-studio/recorder.js (listSessions /
 * loadSession / deleteSession) but simpler: webcam-recorder writes one
 * OPFS file per recording, so we don't need IDB chunk-by-chunk storage.
 * We only use IDB for the per-recording METADATA sidecar (timestamp,
 * duration, size, mime, audio-missing flags) since OPFS files don't
 * have inherent metadata beyond name + bytes.
 *
 * Public surface (window.RecordingsHistory):
 *
 *   list()                  -> Promise<Array<RecordingMeta>>  sorted newest-first
 *   get(fileName)           -> Promise<File>                  reads OPFS bytes as File
 *   save(meta)              -> Promise<void>                  IDB upsert
 *   remove(fileName)        -> Promise<void>                  delete OPFS + IDB
 *   removeAll()             -> Promise<void>                  wipe history
 *   totalSizeBytes()        -> Promise<number>                sum of all
 *   quotaInfo()             -> Promise<{used, quota, available, usagePct}>
 *   autoEvictIfNeeded(thr)  -> Promise<{evicted, freed}>      LRU evict if quota>thr%
 *   downloadAllAsZip(name)  -> Promise<{via,written}>         one archive via __batchDeliver
 *
 *   RecordingMeta = {
 *       fileName: '_webcam-recorder_1700000000_xxx.mp4',
 *       createdAt: 1700000000,  // ms
 *       durMs: 123456,
 *       sizeBytes: 1234567,
 *       mime: 'video/mp4',
 *       ext: 'mp4',
 *       audioMissing: false,    // optional flags
 *       audioDroppedMid: false,
 *       quotaExceededMid: false,
 *       label: null             // optional user-provided name (future feature)
 *   }
 *
 * Lifecycle: WebcamRecorder.stop() calls RecordingsHistory.save(meta) on
 * successful finalize. sweepStaleOpfsRecordings in recorder.js consults
 * IDB before deleting any OPFS file — if it's in history, keep it.
 *
 * Eviction: when OPFS quota crosses 80%, autoEvictIfNeeded() removes
 * oldest recordings until usage drops below 70% (10% hysteresis to
 * prevent thrashing). User-initiated deletes take priority.
 */
(function (w) {
'use strict';
if (w.RecordingsHistory) return;

var DB_NAME = 'timbrica-webcam-recorder-history';
var DB_VERSION = 1;
var STORE = 'recordings';
var FILENAME_PREFIX = '_webcam-recorder_';
var EVICT_TRIGGER_PCT = 80;
var EVICT_TARGET_PCT = 70;

// ─── IndexedDB ───────────────────────────────────────────────────────
var _db = null;
function openDb() {
    if (_db) return Promise.resolve(_db);
    return new Promise(function (resolve, reject) {
        var req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = function (e) {
            var db = e.target.result;
            if (!db.objectStoreNames.contains(STORE)) {
                var store = db.createObjectStore(STORE, { keyPath: 'fileName' });
                store.createIndex('byCreatedAt', 'createdAt', { unique: false });
            }
        };
        req.onsuccess = function () { _db = req.result; resolve(_db); };
        req.onerror = function () { reject(req.error); };
    });
}

function idbGetAll() {
    return openDb().then(function (db) {
        return new Promise(function (resolve, reject) {
            var tx = db.transaction(STORE, 'readonly');
            var store = tx.objectStore(STORE);
            var req = store.getAll();
            req.onsuccess = function () { resolve(req.result || []); };
            req.onerror = function () { reject(req.error); };
        });
    }).catch(function () { return []; });
}
function idbGet(fileName) {
    return openDb().then(function (db) {
        return new Promise(function (resolve, reject) {
            var tx = db.transaction(STORE, 'readonly');
            var req = tx.objectStore(STORE).get(fileName);
            req.onsuccess = function () { resolve(req.result || null); };
            req.onerror = function () { reject(req.error); };
        });
    }).catch(function () { return null; });
}
function idbPut(meta) {
    return openDb().then(function (db) {
        return new Promise(function (resolve, reject) {
            var tx = db.transaction(STORE, 'readwrite');
            var req = tx.objectStore(STORE).put(meta);
            req.onsuccess = function () { resolve(); };
            req.onerror = function () { reject(req.error); };
        });
    });
}
function idbDelete(fileName) {
    return openDb().then(function (db) {
        return new Promise(function (resolve, reject) {
            var tx = db.transaction(STORE, 'readwrite');
            var req = tx.objectStore(STORE).delete(fileName);
            req.onsuccess = function () { resolve(); };
            req.onerror = function () { reject(req.error); };
        });
    });
}
function idbClear() {
    return openDb().then(function (db) {
        return new Promise(function (resolve, reject) {
            var tx = db.transaction(STORE, 'readwrite');
            var req = tx.objectStore(STORE).clear();
            req.onsuccess = function () { resolve(); };
            req.onerror = function () { reject(req.error); };
        });
    });
}

// ─── OPFS helpers ────────────────────────────────────────────────────
function opfsRoot() {
    if (typeof navigator === 'undefined' || !navigator.storage ||
        typeof navigator.storage.getDirectory !== 'function') {
        return Promise.reject(new Error('opfs-unavailable'));
    }
    return navigator.storage.getDirectory();
}

// Walk the MP4 box structure looking for a 'moov' atom via tiny slice-reads:
// a finalized file reaches a 'moov' box; a crash-interrupted one has a stale/
// zero mdat size so the walk runs off the end. Ported from screen-recorder
// history.js — used to mark adopted MP4s `interrupted` so the UI is honest and
// we don't surface an unplayable partial as a normal recording.
async function mp4HasValidMoov(file) {
    try {
        var off = 0, size = file.size, guard = 0;
        while (off + 8 <= size && guard++ < 32) {
            var head = new DataView(await file.slice(off, Math.min(off + 16, size)).arrayBuffer());
            if (head.byteLength < 8) return false;
            var boxSize = head.getUint32(0);
            var type = String.fromCharCode(head.getUint8(4), head.getUint8(5), head.getUint8(6), head.getUint8(7));
            if (type === 'moov') return true;
            if (boxSize === 1) {
                if (head.byteLength < 16) return false;
                boxSize = Number(head.getBigUint64(8));
            } else if (boxSize === 0) {
                return false; // box "extends to EOF" — unfinalized mdat
            }
            if (boxSize < 8) return false;
            off += boxSize;
        }
        return false;
    } catch (_) { return false; }
}

// Discovery + reconciliation: enumerate OPFS files matching our pattern,
// reconcile with IDB. Stale IDB rows (file gone) get removed; orphan
// OPFS files (no IDB row) get adopted with parsed-from-name metadata
// so old recordings from pre-this-feature pipelines aren't lost. Since
// tab-close no longer deletes anything (#939 pagehide-delete removed from
// recorder.js), orphans are interrupted recordings (crash / reload /
// accidental navigation away) or another tab's still-running recording —
// adoption turns both into visible, recoverable history entries.
async function reconcile() {
    var meta = await idbGetAll();
    var byName = {};
    for (var i = 0; i < meta.length; i++) byName[meta[i].fileName] = meta[i];
    var root;
    try { root = await opfsRoot(); } catch (_) { return meta; }

    var opfsNames = {};
    try {
        for await (var entry of root.values()) {
            if (entry.kind !== 'file') continue;
            if (!entry.name.lastIndexOf(FILENAME_PREFIX, 0) === 0) continue;
            if (!/^_webcam-recorder_/.test(entry.name)) continue;
            // Skip Chromium's .crswap atomic-write temp files.
            if (/\.crswap$/.test(entry.name)) continue;
            opfsNames[entry.name] = true;
            if (!byName[entry.name]) {
                // Adopt orphan — parse what we can from the filename.
                var m = entry.name.match(/^_webcam-recorder_(\d+)_/);
                var ts = m ? parseInt(m[1], 10) : Date.now();
                var ext = (entry.name.split('.').pop() || 'mp4').toLowerCase();
                try {
                    var f = await entry.getFile();
                    // Empty / sub-header stub: either an abandoned createWritable()
                    // target whose swap was discarded (mid-recording crash/reload —
                    // OPFS commits only on close(), so the real file stays 0 B) OR
                    // the still-0-B target of a recording LIVE in another tab. Both
                    // must be SKIPPED, not adopted: adopting shows a phantom 0 B
                    // "recording" and deleting it would destroy a concurrent tab's
                    // live target. 0 B costs no quota; the age-gated sweep reclaims
                    // true garbage. 1 KB floor: a valid finalized file is tens of
                    // KB minimum. (Ported from screen-recorder history.js:206.)
                    if (!f || f.size < 1024) continue;
                    // Interrupted-MP4 detection: no valid moov = crashed before
                    // finalize = won't play. WebM partials are cluster-based and
                    // generally playable up to the cut, so they adopt clean.
                    var interrupted = false;
                    if (ext === 'mp4') interrupted = !(await mp4HasValidMoov(f));
                    var adopted = {
                        fileName: entry.name,
                        createdAt: ts,
                        durMs: 0,            // unknown — we'd need to load + probe
                        sizeBytes: f.size,
                        mime: f.type || (ext === 'webm' ? 'video/webm' : 'video/mp4'),
                        ext: ext,
                        audioMissing: false,
                        audioDroppedMid: false,
                        quotaExceededMid: false,
                        label: null,
                        adopted: true,
                        interrupted: interrupted
                    };
                    await idbPut(adopted);
                    byName[entry.name] = adopted;
                } catch (_) {}
            }
        }
    } catch (_) {}

    // Remove IDB rows whose OPFS file vanished (manual delete, browser
    // storage eviction, etc.).
    for (var k in byName) {
        if (Object.prototype.hasOwnProperty.call(byName, k) && !opfsNames[k]) {
            try { await idbDelete(k); } catch (_) {}
            delete byName[k];
        }
    }
    return Object.values(byName);
}

async function list() {
    var rows = await reconcile();
    rows.sort(function (a, b) { return (b.createdAt || 0) - (a.createdAt || 0); });
    return rows;
}

async function get(fileName) {
    var root = await opfsRoot();
    var fh = await root.getFileHandle(fileName, { create: false });
    return await fh.getFile();
}

async function save(meta) {
    if (!meta || !meta.fileName) return;
    meta.createdAt = meta.createdAt || Date.now();
    await idbPut(meta);
}

async function remove(fileName) {
    try {
        var root = await opfsRoot();
        try { await root.removeEntry(fileName); } catch (_) {}
    } catch (_) {}
    await idbDelete(fileName);
}

async function removeAll() {
    var rows = await idbGetAll();
    var root;
    try { root = await opfsRoot(); } catch (_) { root = null; }
    if (root) {
        for (var i = 0; i < rows.length; i++) {
            try { await root.removeEntry(rows[i].fileName); } catch (_) {}
        }
    }
    await idbClear();
}

async function totalSizeBytes() {
    var rows = await idbGetAll();
    var sum = 0;
    for (var i = 0; i < rows.length; i++) sum += rows[i].sizeBytes || 0;
    return sum;
}

async function quotaInfo() {
    /* The REAL grant, not estimate(): on most Chromium builds estimate() says
       usage + 10 GiB whatever is really left, so a full disk never looked full
       here (window.__storageEstimate, timbrica-core.js — support chat 647). */
    var est = window.__storageEstimate ? await window.__storageEstimate() : null;
    if (!est) {
        return { used: 0, quota: Infinity, available: Infinity, usagePct: 0 };
    }
    var quota = est.quota || 0;
    var usage = est.usage || 0;
    return {
        used: usage,
        quota: quota,
        available: Math.max(0, quota - usage),
        usagePct: quota > 0 ? (usage / quota) * 100 : 0
    };
}

// LRU eviction. When usage crosses `triggerPct`, remove oldest recordings
// until below `targetPct`. Returns count + bytes freed.
//
// Two call modes:
//   - Reactive (after save): autoEvictIfNeeded()      → 80% → 70%, gentle
//   - Pre-flight (before start): autoEvictIfNeeded(60, 50) → aggressive,
//     ensures room for a normal recording. Closes 8f56cfd3 today-cluster:
//     OPFS was already at 95%+ at start, autoEvictIfNeeded never ran
//     because it only kicked in AFTER successful save (which never
//     succeeded because close() ran out of quota).
//
// PROTECTED ENTRIES — ported from screen-recorder/history.js (feedback #939
// «запись не нашёл… полная лажа, развод»): the NEWEST recording is never
// evicted, nor anything in opts.protectNames. On a quota-starved machine a
// single recording is most of the grant by itself, and the percent-chasing
// loop deleted the take the user had JUST made. This copy had no such guard;
// it only went unnoticed because on most Chromium builds estimate() reported a
// static usage + 10 GiB and the loop never ran — quotaInfo() now measures the
// real grant (window.__storageEstimate), so it would.
async function autoEvictIfNeeded(triggerPct, targetPct, opts) {
    triggerPct = triggerPct || EVICT_TRIGGER_PCT;
    targetPct = targetPct || EVICT_TARGET_PCT;
    opts = opts || {};
    if (targetPct >= triggerPct) targetPct = triggerPct - 10; // safety
    var q = await quotaInfo();
    if (q.usagePct < triggerPct) return { evicted: 0, freed: 0, unmet: false };
    var rows = await idbGetAll();
    rows.sort(function (a, b) { return (a.createdAt || 0) - (b.createdAt || 0) ; }); // oldest first
    var protectedNames = {};
    var pn = opts.protectNames || [];
    for (var p = 0; p < pn.length; p++) protectedNames[pn[p]] = true;
    if (rows.length) protectedNames[rows[rows.length - 1].fileName] = true; // newest
    var freed = 0, evicted = 0;
    for (var i = 0; i < rows.length; i++) {
        if ((q.usagePct - (freed / q.quota) * 100) < targetPct) break;
        var r = rows[i];
        if (protectedNames[r.fileName]) continue;
        try { await remove(r.fileName); } catch (_) {}
        freed += r.sizeBytes || 0;
        evicted++;
    }
    var unmet = (q.usagePct - (freed / q.quota) * 100) >= targetPct;
    return { evicted: evicted, freed: freed, unmet: unmet };
}

// Batch download through window.__batchDeliver — one archive, the measured
// ceiling (a history of recordings is big, and the JSZip this replaced built
// the whole archive in the heap next to every recording), and DISTINCT names:
// the label was stamped to the minute, so two takes in one minute carried one
// name and the second silently replaced the first inside the archive.
async function downloadAllAsZip(suggestedName) {
    var rows = await list();
    if (rows.length === 0) throw new Error('history-empty');
    var items = [];
    for (var i = 0; i < rows.length; i++) {
        var r = rows[i];
        try {
            var f = await get(r.fileName);
            var label = (r.label || 'recording') + '_' + new Date(r.createdAt).toISOString().slice(0, 19).replace(/[:T]/g, '-') + '.' + r.ext;
            items.push({ name: label, blob: f });
        } catch (_) {}
    }
    var BD = w.__batchDeliver || (w.__ensureBatchDeliver ? await w.__ensureBatchDeliver() : null);
    if (!BD) throw new Error('batch-deliver-unavailable');
    return BD.deliver(items, { slug: 'webcam-recorder', zipName: (suggestedName || 'webcam-recordings') + '.zip', archiveOnly: true });
}

w.RecordingsHistory = {
    list: list,
    get: get,
    save: save,
    remove: remove,
    removeAll: removeAll,
    totalSizeBytes: totalSizeBytes,
    quotaInfo: quotaInfo,
    autoEvictIfNeeded: autoEvictIfNeeded,
    downloadAllAsZip: downloadAllAsZip
};

})(window);
